import React, { useState, useEffect, useLayoutEffect } from 'react';
import cn from 'classnames';
import ShortsCard from './ShortsCard';
import ShortsModal from './ShortsModal';
import { getRegularCardWidth, getOffset } from './utils';
import { useStateModal } from 'utils/hooks';
import { observer, insightsShorts as store } from 'store';

import * as styles from './DesktopLayout.module.scss';

function DesktopSlider({ items, theme, activeTab }) {
  const { modalState, showModal, hideModal } = useStateModal();
  const isThemeDark = theme === 'dark';
  const transitionSpeed = 700;
  const stateSlides = [...items.slice(-4), ...items, ...items.slice(0, 4)];
  const [current, setCurrent] = useState(4);
  const [disableTransition, setDisableTransition] = useState(true);
  const [disableArrows, setDisableArrows] = useState(false);
  const [translateX, setTranslateX] = useState(414);
  const [globalPlayback, setGlobalPlayback] = useState(false);

  const style = {
    '--translateX': `-${translateX}px`,
    '--transitionSpeed': `${transitionSpeed}ms`,
  };

  const calculateTranslate = index => {
    const cardWidth = getRegularCardWidth();
    const offset = getOffset();

    const translateOffset = Math.floor(
      (stateSlides.length - stateSlides.slice(index).length) * cardWidth - offset,
    );
    setTranslateX(translateOffset);
  };

  useLayoutEffect(() => {
    calculateTranslate(4);
  }, []);

  useEffect(() => {
    const calculateTranslateOnResize = () => {
      setDisableTransition(true);
      calculateTranslate(current);
    };

    window.addEventListener('resize', calculateTranslateOnResize);

    return () => {
      window.removeEventListener('resize', calculateTranslateOnResize);
    };
  }, [current]);

  useEffect(() => {
    // adjust slider position while fullscreen modal is active to show the correct slide after exiting fullscreen
    if (store.currentIndex && modalState.isOpen) {
      setDisableTransition(true);
      setCurrent(store.currentIndex + 4);

      calculateTranslate(store.currentIndex + 4);
    }
  }, [store.currentIndex, modalState.isOpen]);

  useEffect(() => {
    if (disableArrows) {
      setTimeout(() => {
        setDisableArrows(false);
      }, transitionSpeed);
    }
  }, [disableArrows]);

  useEffect(() => {
    if (current === stateSlides.length - 3) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(5);
        store.setCurrentIndex(1);
        calculateTranslate(5);

        // play videos in slides that are outside items length range after transition is complete
        if (globalPlayback) {
          store.setPlaying(true);
        }
      }, transitionSpeed);
    }
    if (current === stateSlides.length - 4) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(4);
        store.setCurrentIndex(0);
        calculateTranslate(4);

        // play videos in slides that are outside items length range after transition is complete
        if (globalPlayback) {
          store.setPlaying(true);
        }
      }, transitionSpeed);
    }
    if (current === 3) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(stateSlides.length - 5);
        store.setCurrentIndex(items.length - 1);
        calculateTranslate(stateSlides.length - 5);

        // play videos in slides that are outside items length range after transition is complete
        if (globalPlayback) {
          store.setPlaying(true);
        }
      }, transitionSpeed);
    }
    if (current === 2) {
      setTimeout(() => {
        setDisableTransition(true);
        setCurrent(stateSlides.length - 6);
        store.setCurrentIndex(items.length - 2);
        calculateTranslate(stateSlides.length - 6);

        // play videos in slides that are outside items length range after transition is complete
        if (globalPlayback) {
          store.setPlaying(true);
        }
      }, transitionSpeed);
    }
  }, [current]);

  const handleClick = index => {
    if (index === current) {
      return;
    }

    setDisableArrows(true);

    if (disableTransition) {
      setDisableTransition(false);
    }

    calculateTranslate(index);
    setCurrent(index);
    store.resetShortsStore();
    store.setCurrentIndex(index - 4);

    // play videos in slides that are within items length range
    if (index - 4 >= 0 && index - 4 <= items.length - 1 && globalPlayback) {
      store.setPlaying(true);
    }
  };

  const handleNext = () => {
    handleClick(current + 1);
  };

  const handlePrev = () => {
    handleClick(current - 1);
  };

  const shouldLoadVideo = index => {
    // always add video sources for first two and last two videos for infinite playback
    return (
      index - 4 === 0 ||
      index - 4 === 1 ||
      index - 4 === items.length - 1 ||
      index - 4 === items.length - 2
    );
  };

  return (
    <>
      <div id="cardsWrapper" className={styles.cardsWrapper}>
        <div
          className={cn(styles.cardsTrack, !disableTransition && styles.withTransition)}
          style={style}
        >
          {stateSlides.map((item, i) => (
            <ShortsCard
              key={`${item.title}-${i}`}
              disableTransition={disableTransition}
              active={i === current}
              loadVideo={shouldLoadVideo(i)}
              handleClick={() => handleClick(i)}
              openModal={showModal}
              handleNext={handleNext}
              isThemeDark={isThemeDark}
              activeTab={activeTab}
              globalPlayback={globalPlayback}
              setGlobalPlayback={setGlobalPlayback}
              fullscreen={modalState.isOpen}
              {...item}
            />
          ))}
        </div>
      </div>
      <button
        className={cn(styles.navArrow, styles.prev, isThemeDark && styles.light)}
        onClick={handlePrev}
        disabled={disableArrows}
        aria-label="Previous Slide"
      />
      <button
        className={cn(styles.navArrow, styles.next, isThemeDark && styles.light)}
        onClick={handleNext}
        disabled={disableArrows}
        aria-label="Next Slide"
      />
      {modalState.isOpen && (
        <ShortsModal isOpen={modalState.isOpen} closeModalCallback={hideModal} items={items} />
      )}
    </>
  );
}

export default observer(DesktopSlider);
